import { getDownloadURL, ref } from 'firebase/storage';
import type { ImageSourcePropType } from 'react-native';
import { storage } from '../firebase';
import type { TonyteActivity } from '../types';
import { EVENT_IMAGE_ASSETS } from '../assets/eventImageAssets';

type RNImageSourceValue = number | string;

type ActivityImageFields = TonyteActivity & Record<string, unknown>;

type ImageCandidate = {
  field: string;
  value: string;
};

export type ResolvedActivityImage = {
  source: RNImageSourceValue | null;
  selectedField: string | null;
  rawValue: string;
  resolvedValue: RNImageSourceValue | null;
  sourceType: 'remote_url' | 'firebase_storage' | 'local_asset' | 'web_fallback' | 'missing';
};

const HOSTED_ASSET_BASE = 'https://tonyte.app';
const UNSPLASH_BASE = 'https://images.unsplash.com/';
const downloadUrlCache = new Map<string, Promise<string>>();

const IMAGE_FIELD_PRIORITY = [
  'imageUrl',
  'coverPhoto',
  'coverImageUrl',
  'image',
  'photoUrl',
  'imageURL',
  'backgroundImageUrl',
  'imagePath',
  'coverPhotoPath',
  'storagePath',
  'coverImage',
  'coverUrl',
  'thumbnailUrl',
  'posterUrl',
  'poster',
  'mediaUrl',
  'assetsImage',
  'categoryImage',
] as const;

const ACTIVITY_FALLBACK_IMAGES = {
  jam: [
    `${UNSPLASH_BASE}photo-1511379938547-c1f69419868d?auto=format&fit=crop&q=80&w=1200`,
    `${UNSPLASH_BASE}photo-1516280440614-37939bbacd81?auto=format&fit=crop&q=80&w=1200`,
    `${UNSPLASH_BASE}photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&q=80&w=1200`,
  ],
  club: [
    `${UNSPLASH_BASE}photo-1571266028243-d220c9c3b5bf?auto=format&fit=crop&q=80&w=1200`,
    `${UNSPLASH_BASE}photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&q=80&w=1200`,
    `${UNSPLASH_BASE}photo-1501386761578-eac5c94b800a?auto=format&fit=crop&q=80&w=1200`,
  ],
  student: [
    `${UNSPLASH_BASE}photo-1529156069898-49953e39b3ac?auto=format&fit=crop&q=80&w=1200`,
    `${UNSPLASH_BASE}photo-1517457373958-b7bdd4587205?auto=format&fit=crop&q=80&w=1200`,
    `${UNSPLASH_BASE}photo-1522202176988-66273c2fd55f?auto=format&fit=crop&q=80&w=1200`,
  ],
  outdoor: [
    `${UNSPLASH_BASE}photo-1529156069898-49953e39b3ac?auto=format&fit=crop&q=80&w=1200`,
    `${UNSPLASH_BASE}photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&q=80&w=1200`,
    `${UNSPLASH_BASE}photo-1506744038136-46273834b3fb?auto=format&fit=crop&q=80&w=1200`,
  ],
  sports: [
    `${UNSPLASH_BASE}photo-1517836357463-d25dfeac3438?auto=format&fit=crop&q=80&w=1200`,
    `${UNSPLASH_BASE}photo-1518611012118-696072aa579a?auto=format&fit=crop&q=80&w=1200`,
    `${UNSPLASH_BASE}photo-1517649763962-0c623066013b?auto=format&fit=crop&q=80&w=1200`,
  ],
  food: [
    `${UNSPLASH_BASE}photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&q=80&w=1200`,
    `${UNSPLASH_BASE}photo-1414235077428-338989a2e8c0?auto=format&fit=crop&q=80&w=1200`,
    `${UNSPLASH_BASE}photo-1559339352-11d035aa65de?auto=format&fit=crop&q=80&w=1200`,
  ],
  culture: [
    `${UNSPLASH_BASE}photo-1518998053901-5348d3961a04?auto=format&fit=crop&q=80&w=1200`,
    `${UNSPLASH_BASE}photo-1511578314322-379afb476865?auto=format&fit=crop&q=80&w=1200`,
    `${UNSPLASH_BASE}photo-1460661419201-fd4cecdf8a8b?auto=format&fit=crop&q=80&w=1200`,
  ],
  default: [
    `${UNSPLASH_BASE}photo-1517457373958-b7bdd4587205?auto=format&fit=crop&q=80&w=1200`,
    `${UNSPLASH_BASE}photo-1529156069898-49953e39b3ac?auto=format&fit=crop&q=80&w=1200`,
    `${UNSPLASH_BASE}photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&q=80&w=1200`,
  ],
};

const isRemoteUrl = (value: string) => /^https?:\/\//i.test(value) || /^data:/i.test(value);

const isStoragePath = (value: string) =>
  /^gs:\/\//i.test(value) ||
  /^(\/)?(activities|events|officialEvents|images|event-images|eventImages|official-events)\//i.test(value);

function getFirebaseStoragePathFromUrl(value: string): string | null {
  try {
    const url = new URL(value);
    if (!url.hostname.includes('firebasestorage.googleapis.com')) return null;
    const marker = '/o/';
    const markerIndex = url.pathname.indexOf(marker);
    if (markerIndex < 0) return null;
    const encodedPath = url.pathname.slice(markerIndex + marker.length);
    return decodeURIComponent(encodedPath).replace(/^\/+/, '') || null;
  } catch {
    return null;
  }
}

function getCandidateFilename(value: string): string {
  const clean = value.split('?')[0].split('#')[0];
  const lastPart = clean.substring(clean.lastIndexOf('/') + 1);
  try {
    return decodeURIComponent(lastPart);
  } catch {
    return lastPart;
  }
}

function getBundledEventImage(value: string): number | null {
  const filename = getCandidateFilename(value);
  const matched = EVENT_IMAGE_ASSETS[filename];
  return typeof matched === 'number' ? matched : null;
}

function getImageCandidate(activity: TonyteActivity): ImageCandidate | null {
  const fields = activity as ActivityImageFields;
  for (const field of IMAGE_FIELD_PRIORITY) {
    const value = fields[field];
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    // A device-local path (an old app-editor bug saved these) only exists on
    // the phone that picked it — never use it for anyone.
    if (/^(file|content|ph|assets-library):\/\//i.test(trimmed)) continue;
    if (trimmed) return { field, value: trimmed };
  }
  return null;
}

function normalizeStoragePath(value: string): string {
  if (/^gs:\/\//i.test(value)) return value;
  return value.replace(/^\/+/, '');
}

async function getCachedDownloadUrl(storagePath: string): Promise<string> {
  const normalizedPath = normalizeStoragePath(storagePath);
  if (!downloadUrlCache.has(normalizedPath)) {
    downloadUrlCache.set(normalizedPath, getDownloadURL(ref(storage, normalizedPath)));
  }
  return downloadUrlCache.get(normalizedPath)!;
}

function pickFallbackTheme(activity: TonyteActivity): keyof typeof ACTIVITY_FALLBACK_IMAGES {
  const tokenText = [
    activity.title,
    activity.description,
    activity.category,
    activity.entryType,
    activity.sourceType,
    activity.locationName,
    activity.venueName,
    activity.clubName,
    ...(activity.tags || []),
    ...(activity.musicGenres || []),
    ...(activity.lineup || []),
  ].map(value => String(value || '').toLowerCase()).join(' ');

  if (activity.sourceType === 'generated_jam_session' || /jam|open mic|session|acoustic|live music|instrument/.test(tokenText)) return 'jam';
  if (activity.sourceType === 'club' || /club|nightlife|dj|dancefloor|party|techno|house|electronic/.test(tokenText)) return 'club';
  if (/student|erasmus|campus|welcome week|welcome party|language exchange|cafe lingua/.test(tokenText)) return 'student';
  if (/hike|walk|picnic|park|outdoor|saxon switzerland|city walk/.test(tokenText)) return 'outdoor';
  if (/sport|football|run|fitness|yoga|gym|tennis|workout/.test(tokenText)) return 'sports';
  if (/dinner|drinks|bar|cocktail|restaurant|brunch|coffee|cafe/.test(tokenText)) return 'food';
  if (activity.category === 'culture' || /gallery|museum|art|creative|concert|theatre|culture/.test(tokenText)) return 'culture';
  return 'default';
}

// Exported so EventCard.tsx/EventDetailScreen.tsx can call this directly as
// a last-resort <Image onError> fallback — resolveActivityImage() already
// uses this internally for the "no image field at all" case, but a
// resolved URL that 404s (or, as found live, returns a 200 HTML page
// instead of the expected image — tonyte.app's own hosting serving its SPA
// fallback for a since-deleted/never-uploaded /event-images/ file) needs
// the exact same graceful fallback, not just a blank gap where the hero/
// card image should be. 100% shared logic — fixes native identically.
export function getWebFallbackImage(activity: TonyteActivity): string {
  const theme = pickFallbackTheme(activity);
  const images = ACTIVITY_FALLBACK_IMAGES[theme];
  const seed = `${activity.id}:${activity.title}:${theme}`;
  const hash = seed.split('').reduce((total, character) => total + character.charCodeAt(0), 0);
  return images[hash % images.length];
}

export function getActivityImageCandidate(activity: TonyteActivity): string {
  return getImageCandidate(activity)?.value || getWebFallbackImage(activity);
}

export async function resolveActivityImage(activity: TonyteActivity): Promise<ResolvedActivityImage> {
  const candidate = getImageCandidate(activity);

  if (!candidate) {
    const fallback = getWebFallbackImage(activity);
    return {
      source: fallback,
      selectedField: 'webFallbackVisual',
      rawValue: fallback,
      resolvedValue: fallback,
      sourceType: 'web_fallback',
    };
  }

  const localAsset = getBundledEventImage(candidate.value);
  if (localAsset) {
    return {
      source: localAsset,
      selectedField: candidate.field,
      rawValue: candidate.value,
      resolvedValue: localAsset,
      sourceType: 'local_asset',
    };
  }

  if (isRemoteUrl(candidate.value)) {
    const firebaseStoragePath = getFirebaseStoragePathFromUrl(candidate.value);
    if (firebaseStoragePath && !candidate.value.includes('token=')) {
      const resolved = await getCachedDownloadUrl(firebaseStoragePath);
      return {
        source: resolved,
        selectedField: candidate.field,
        rawValue: candidate.value,
        resolvedValue: resolved,
        sourceType: 'firebase_storage',
      };
    }

    const resolved = candidate.value;
    return {
      source: resolved,
      selectedField: candidate.field,
      rawValue: candidate.value,
      resolvedValue: resolved,
      sourceType: 'remote_url',
    };
  }

  if (candidate.value.startsWith('/assets/') || candidate.value.startsWith('assets/')) {
    const resolved = encodeURI(`${HOSTED_ASSET_BASE}/${candidate.value.replace(/^\/+/, '')}`);
    return {
      source: resolved,
      selectedField: candidate.field,
      rawValue: candidate.value,
      resolvedValue: resolved,
      sourceType: 'remote_url',
    };
  }

  if (isStoragePath(candidate.value)) {
    const resolved = await getCachedDownloadUrl(candidate.value);
    return {
      source: resolved,
      selectedField: candidate.field,
      rawValue: candidate.value,
      resolvedValue: resolved,
      sourceType: 'firebase_storage',
    };
  }

  const hostedPath = encodeURI(`${HOSTED_ASSET_BASE}/${candidate.value.replace(/^\/+/, '')}`);
  return {
    source: hostedPath,
    selectedField: candidate.field,
    rawValue: candidate.value,
    resolvedValue: hostedPath,
    sourceType: 'remote_url',
  };
}

export async function resolveActivityImageSource(activity: TonyteActivity): Promise<ImageSourcePropType | null> {
  const source = (await resolveActivityImage(activity)).source;
  if (!source) return null;
  return typeof source === 'number' ? source : { uri: source };
}
